<template>
    <form id="search" class="form" role="search" @submit.prevent="search" v-click-outside="handleBlur">
        <div id="search__form">
            <label for="search__query" class="sr-only">Search site</label>
            <input
                id="search__query"
                type="search"
                name="query"
                class="input"
                placeholder="Search site"
                v-model="query"
                autocomplete="off"
                @focus="handleFocus"
                ref="input"
            />
            <button type="submit" class="button" aria-label="Search">
                <Spinner v-if="$wait.is('loading_search_results')" :height="20" :width="20" color="#fff"/>
                <SvgIcon v-else icon="search"/>
            </button>
        </div>
        <div id="search__results" v-if="showResults">
            <section class="result-category" v-if="results.opportunities && results.opportunities.length > 0">
                <header class="result-category__header">
                    <span>Opportunities</span>
                    <router-link :to="`/search/${query}/opportunities`">View All</router-link>
                </header>
                <ul class="result-category__results">
                    <li v-for="opportunity in results.opportunities">
                        <router-link :to="{ name: 'view-opportunity', params: { opportunity_id: opportunity.id } }">
                            {{ formatOppName(opportunity) }}
                        </router-link>
                    </li>
                </ul>
            </section>
            <section class="result-category" v-if="results.madds && results.madds.length > 0">
                <header class="result-category__header">
                    <span>MADDs</span>
                    <router-link :to="`/search/${query}/madds`">View All</router-link>
                </header>
                <ul class="result-category__results">
                    <li v-for="madd in results.madds">
                        <router-link :to="{ name: 'view-madd', params: { madd_id: madd.id } }">
                            {{ madd.companyName }}
                        </router-link>
                    </li>
                </ul>
            </section>
            <section class="result-category" v-if="results.users && results.users.length > 0">
                <header class="result-category__header">
                    <span>Users</span>
                    <router-link :to="`/search/${query}/users`">View All</router-link>
                </header>
                <ul class="result-category__results">
                    <li v-for="user in results.users">
                        <router-link :to="{ name: 'view-user', params: { user_id: user.id } }">
                            {{ user.name }}
                        </router-link>
                    </li>
                </ul>
            </section>
        </div>
    </form>
</template>
<script>
import debounce from 'debounce';
import SvgIcon from '@components/SvgIcon';
import API from '@api';
import Spinner from '@components/Spinner.vue';

export default {
    name: 'HeaderSearch',
    components: { Spinner, SvgIcon },
    props: {
        defaultValue: {
            type: String,
            default: null,
        },
        typeahead: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            results: {
                opportunities: [],
                madds: [],
                users: [],
            },
            isFocused: false,
            query: this.defaultValue ? this.defaultValue : '',
        }
    },
    methods: {
        handleChange: () => {},
        async fetchResource() {
            if(this.$wait.is('loading_search_results')) {
                return;
            }

            try {
                this.$wait.start('loading_search_results');

                const data = await API.search(this.query);

                this.results = data;
                this.isFocused = true;
            } catch(err) {
                throw new Error(err.message);
            } finally {
                this.$wait.end('loading_search_results');
            }
        },
        formatOppName(opportunity) {
            if(!opportunity.projectName){
                return opportunity.companyName ?? '-';
            }

            return opportunity.projectName;
        },
        search() {
            this.$router.push({
                name: 'search',
                params: {
                    query: this.query,
                    tab: this.$route.params.tab ?? 'opportunities'
                }
            });
        },
        handleFocus(ev) {
            this.isFocused = true;
        },
        handleBlur(ev) {
            this.isFocused = false;
        }
    },
    computed: {
        showResults() {
            // console.log({
            //     typeahead: this.typeahead,
            //     isFocused: this.isFocused,
            //     isLoading: !this.$wait.is('loading_search_results'),
            //     hasResults: (
            //         this.results.opportunities.length > 0 ||
            //         this.results.users.length > 0 ||
            //         this.results.madds.length > 0
            //     ),
            //     query: this.query && this.query.length > 0
            // });

            return (
                this.typeahead &&
                this.isFocused &&
                !this.$wait.is('loading_search_results') &&
                (
                    this.results.opportunities.length > 0 ||
                    this.results.users.length > 0 ||
                    this.results.madds.length > 0
                ) &&
                (this.query && this.query.length > 0)
            );
        },
    },
    watch: {
        query: {
            handler: function() {
                this.handleChange();
            },
            deep: true,
        },
    },
    created() {
        if(this.typeahead) {
            this.handleChange = debounce(
                this.fetchResource.bind(this),
                550,
            );
        }
    },
}
</script>

<style lang="scss" scoped>
@import '~reflex-grid/scss/includes/variables';
@import '~reflex-grid/scss/includes/mixins';
@import '~@/theme/_variables.scss';
@import '~@/mixins/_class.scss';
@import '~@/theme/_extenables.scss';

#search {
    position: relative;
    flex: 0 0 auto;
    width: 100%;

    #search__form {
        display: flex;
        position: relative;
        z-index: 11;

        .input {
            background-color: #F1F1F1;
            border-radius: 1.8rem;
            border: none;
            width: 100%;
            font-style: normal;
            font-family: "Axiforma", sans-serif;
            font-weight: 300;
            font-size: 1.4rem;
            padding: 0.8rem 2rem;
            line-height: 1.9rem;
            position: relative;
            z-index: 5;
        }

        .button {
            appearance: none;
            -webkit-appearance: none;
            background-color: $accent;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 3.6rem;
            width: 3.6rem;
            border-radius: 50%;
            border: none;
            position: relative;
            cursor: pointer;
            z-index: 10;
            flex: 0 0 3.6rem;
            margin-left: -3.6rem;

            &:hover {
                background-color: #0E716D;
            }

            svg {
                height: 1.6rem;
                width: 1.6rem;
                margin-right: 0;
                color: white;
            }
        }
    }

    #search__results {
        position: absolute;
        right: 0;
        top: calc(100% - 2rem);
        background: white;
        background: linear-gradient(180deg, #F1F1F1 0%, #F1F1F1 2rem, white 2rem, white 100%);
        padding-top: 2rem;
        border-radius: .5rem;
        box-shadow: 0 .6rem 1rem 0 rgba(49, 49, 74, 0.08);
        min-width: 30rem;
        overflow: hidden;
    }

    @include respond-to(medium) {
        margin-left: 0;
    }
}

.result-category {
    .result-category__header {
        display: flex;
        justify-content: space-between;
        padding: 0.8rem 1rem;
        background-color: #F1F1F1;

        span {
            color: black;
            font-weight: bold;
        }
    }

    .result-category__results {
        padding: 0;
        list-style: none;
        margin: .5rem auto;

        li {
            padding: .5rem 1rem;
        }
    }
}
</style>
